import {
  all,
  call,
  put,
  select,
  takeEvery,
  takeLatest,
} from 'redux-saga/effects';

import { get, post, putRequest } from '../../../utils/request';
import { chooseFileSaga, processFileSaga, uploadDocumentSaga } from '../../../utils/docUpload';
import { updateUserProfile } from '../../Main/actions';
import makeSelectMain from '../../Main/selectors';
import makeSelectMobile from '../../Mobile/selectors';
import {
  changeEmailFailure,
  changeEmailSuccess,
  changeEmailValidationFailure,
  changeMobileNumberFailure,
  changeMobileNumberSuccess,
  changeMobileNumberValidationFailure,
  changePasswordFailure,
  changePasswordSuccess,
  changePasswordValidationFailure,
  changeStep,
  chooseDocumentCancellation,
  chooseDocumentFailure,
  chooseDocumentSuccess,
  closeModalState,
  fetchLegalNameMilitaryStatusFailure,
  fetchLegalNameMilitaryStatusSuccess,
  fetchMobileNumberStatusFailure,
  fetchMobileNumberStatusSuccess,
  fetchProfileFailure,
  fetchProfileSuccess,
  openModalState,
  processDocument,
  processDocumentFailure,
  processDocumentSuccess,
  uploadDocumentFailure,
  uploadDocumentSuccess,
  verifyMobileNumberFailure,
  verifyMobileNumberSuccess,
  verifyMobileNumberValidationFailure,
} from './actions';
import {
  CHANGE_EMAIL,
  CHANGE_EMAIL_ERROR_MESSAGE,
  CHANGE_MOBILE_NUMBER,
  CHANGE_MOBILE_NUMBER_ERROR_MESSAGE,
  CHANGE_PASSWORD,
  CHOOSE_DOCUMENT,
  ERROR_MESSAGES,
  FETCH_LEGAL_NAME_MILITARY_STATUS,
  FETCH_MOBILE_NUMBER_STATUS,
  FETCH_MOBILE_NUMBER_STATUS_ERROR_MESSAGE,
  FETCH_PROFILE,
  FETCH_PROFILE_ERROR_MESSAGE,
  PASSWORD_ERROR_MESSAGES,
  PROCESS_DOCUMENT,
  RESEND_PASSCODE_SUCCESS_MESSSAGE,
  SMS_ERROR_MESSAGES,
  UPLOAD_DOCUMENT,
  VERIFY_MOBILE_NUMBER,
  VERIFY_MOBILE_NUMBER_ERROR_MESSAGE,
  VERIFY_MOBILE_NUMBER_SUCCESS_MESSAGE,
} from './constants';

export function* changeEmailSaga({ baseUrl, navigationHandler }, { payload }) {
  try {
    const isMobile = yield select(makeSelectMobile('isMobile'));
    const { newEmail } = payload;
    const url = `${baseUrl}/settings/email`;
    const { error, profile } = yield call(putRequest, url, { newEmail });
    if (error) {
      const { reasons } = error;
      yield put(changeEmailValidationFailure(ERROR_MESSAGES[reasons[0]]));
    } else {
      const { currentSetting } = profile;
      yield put(updateUserProfile(currentSetting));
      yield put(changeEmailSuccess(currentSetting));
      if (isMobile) {
        yield call(navigationHandler, 'EditEmailConfirmation');
      } else {
        yield put(changeStep(2));
      }
    }
  } catch {
    yield put(changeEmailFailure(CHANGE_EMAIL_ERROR_MESSAGE));
  }
}

export function* changeMobileNumberSaga({ baseUrl, navigationHandler }, { payload }) {
  try {
    const isMobile = yield select(makeSelectMobile('isMobile'));
    const { isVerify, mobileNumber } = payload;
    const url = `${baseUrl}/settings/mobilenumber`;
    const {
      allowedOperation,
      error,
      status,
      token,
    } = yield call(post, url, { mobileNumber });
    if (error) {
      const { reasons } = error;
      yield put(changeMobileNumberValidationFailure(SMS_ERROR_MESSAGES[reasons[0]]));
    } else {
      const currentSetting = { allowedOperation, status, token };
      yield put(changeMobileNumberSuccess({
        currentSetting,
        message: isVerify ? RESEND_PASSCODE_SUCCESS_MESSSAGE : '',
      }));
      if (isMobile && !isVerify) {
        yield call(navigationHandler, 'VerifyMobileNumber');
      } else if (!isVerify) {
        yield put(openModalState({ modalState: 'verifyMobileNumber' }));
      }
    }
  } catch (error) {
    yield put(changeMobileNumberFailure(CHANGE_MOBILE_NUMBER_ERROR_MESSAGE));
  }
}

export function* changePasswordSaga({ baseUrl }, { payload }) {
  try {
    const { newPassword, oldPassword } = payload;
    const url = `${baseUrl}/settings/password`;
    const { error } = yield call(putRequest, url, { newPassword, oldPassword });
    if (error) {
      const { reasons } = error;
      const { field, message } = PASSWORD_ERROR_MESSAGES[reasons[0]];
      yield put(changePasswordValidationFailure({ field, message }));
    } else {
      yield put(changePasswordSuccess());
      yield put(changeStep(2));
    }
  } catch {
    yield put(changePasswordFailure());
    yield put(changeStep(3));
  }
}

export function* fetchLegalNameMilitaryStatusSaga({ baseUrl }) {
  try {
    const loanNumber = yield select(makeSelectMain('selectedLoan'));
    const endpoint = `${baseUrl}/settings/legalnamemilitarystatus/${loanNumber}`;
    const { legalName, militaryServiceStatus } = yield call(get, endpoint);
    yield put(fetchLegalNameMilitaryStatusSuccess({ legalName, militaryServiceStatus }));
  } catch {
    yield put(fetchLegalNameMilitaryStatusFailure(FETCH_PROFILE_ERROR_MESSAGE));
  }
}

export function* fetchMobileNumberStatusSaga({ baseUrl }) {
  try {
    const endpoint = `${baseUrl}/settings/sms/status`;
    const {
      allowedOperation,
      currentSetting,
      error,
      status,
    } = yield call(get, endpoint);
    if (error) {
      yield put(fetchMobileNumberStatusFailure(FETCH_MOBILE_NUMBER_STATUS_ERROR_MESSAGE));
    } else {
      yield put(fetchMobileNumberStatusSuccess({ allowedOperation, currentSetting, status }));
    }
  } catch {
    yield put(fetchMobileNumberStatusFailure(FETCH_MOBILE_NUMBER_STATUS_ERROR_MESSAGE));
  }
}

export function* fetchProfileSaga({ baseUrl }) {
  try {
    const endpoint = `${baseUrl}/settings/profile`;
    const { error, profile: { currentSetting } } = yield call(get, endpoint);
    if (error) {
      yield put(fetchProfileFailure(FETCH_PROFILE_ERROR_MESSAGE));
    } else {
      yield put(fetchProfileSuccess(currentSetting));
    }
  } catch {
    yield put(fetchProfileFailure(FETCH_PROFILE_ERROR_MESSAGE));
  }
}

export function* verifyMobileNumberSaga({ baseUrl, navigationHandler }, { payload }) {
  try {
    const isMobile = yield select(makeSelectMobile('isMobile'));
    const { mobileNumber, passCode, token } = payload;
    const url = `${baseUrl}/settings/mobilenumber`;
    const body = { mobileNumber, passCode, token };
    const { error, profile } = yield call(putRequest, url, body);
    if (error) {
      const { reasons } = error;
      yield put(verifyMobileNumberValidationFailure(SMS_ERROR_MESSAGES[reasons[0]]));
    } else {
      const { currentSetting } = profile;
      yield put(updateUserProfile(currentSetting));
      yield put(verifyMobileNumberSuccess({
        currentSetting,
        message: VERIFY_MOBILE_NUMBER_SUCCESS_MESSAGE,
      }));
      if (isMobile) {
        yield call(navigationHandler, 'EditMobileNumberConfirmation');
      } else {
        yield put(changeStep(2));
        yield put(closeModalState());
      }
    }
  } catch (error) {
    yield put(verifyMobileNumberFailure(VERIFY_MOBILE_NUMBER_ERROR_MESSAGE));
  }
}

export default (options) => function* watcherSaga() {
  yield all([
    takeLatest(CHANGE_EMAIL, changeEmailSaga, options),
    takeEvery(CHANGE_MOBILE_NUMBER, changeMobileNumberSaga, options),
    takeLatest(CHANGE_PASSWORD, changePasswordSaga, options),
    takeLatest(
      CHOOSE_DOCUMENT,
      chooseFileSaga,
      {
        ...options,
        chooseDocumentCancellation,
        chooseDocumentFailure,
        chooseDocumentSuccess,
        processDocument,
      },
    ),
    takeLatest(FETCH_LEGAL_NAME_MILITARY_STATUS, fetchLegalNameMilitaryStatusSaga, options),
    takeLatest(FETCH_MOBILE_NUMBER_STATUS, fetchMobileNumberStatusSaga, options),
    takeLatest(FETCH_PROFILE, fetchProfileSaga, options),
    takeLatest(
      UPLOAD_DOCUMENT,
      uploadDocumentSaga,
      {
        ...options,
        uploadDocumentFailure,
        uploadDocumentSuccess,
      },
    ),
    takeLatest(
      PROCESS_DOCUMENT,
      processFileSaga,
      {
        ...options,
        processDocumentFailure,
        processDocumentSuccess,
      },
    ),
    takeLatest(VERIFY_MOBILE_NUMBER, verifyMobileNumberSaga, options),
  ]);
};
